import { isUpdatable } from '@/pages/SubscriptionSettingsPage/subscription-display';
import { SeatType, seatTypeFor } from '@/pages/SubscriptionSettingsPage/vessel-seats';
import type { SeatPermissions } from '@/store/permissions/permissions.dto';
import { type Subscription, SubscriptionTier } from '@/store/subscriptions/subscriptions.types';

/**
 * Why a permission cannot be switched on here.
 *
 * The People service gates permissions by *seat*, not by plan directly: every seat type
 * has an allow-list (`GET /permissions/seats`), reads filter a person's `rights` by the
 * allow-list of the seat they hold, and writes merge anything outside it straight back in.
 * A permission the relevant list omits therefore cannot be granted or revoked at all — the
 * only honest thing the UI can do is disable it and say which of the two walls it is behind.
 *
 * - `plan` — the vessel's own tier does not reach it. Buying a seat would not help; the
 *   subscription has to move up.
 * - `seat` — the tier does reach it, but this person is not in a management seat. Moving
 *   them into one unlocks it without touching the plan.
 *
 * A role is always measured against the vessel's management seat, so a role row is never
 * `seat`-gated: there is no person, and therefore no seat, to move.
 */
export type GateReason = 'plan' | 'seat';

export interface PermissionGates {
  /** `null` when the permission is free to toggle. */
  reasonFor: (name: string) => GateReason | null;
  /**
   * Whether moving up a tier would actually unlock it. Ten permissions the page can show
   * — `Notifications`, `RoomsBasic`, `DocumentBasic` and the like — appear in no seat
   * allow-list at all, so no plan carries them and an upgrade would change nothing. They
   * still read as unavailable; they just must not be advertised as an upgrade away.
   */
  unlockableByUpgrade: (name: string) => boolean;
  /** Whether any gate is in force at all — false until the allow-lists have loaded. */
  active: boolean;
  /** The tier whose management seat the vessel's plan buys; `null` while nothing is gated. */
  planTier: SubscriptionTier | null;
  /**
   * The other tiers whose management seat carries the permissions — all of them, or any one
   * for a surface that opens on any. This is what a lock names as the plan to move to, read
   * from the allow-lists rather than written into the copy. Empty when no tier does.
   */
  plansCarrying: (names: readonly string[], match?: 'all' | 'any') => SubscriptionTier[];
}

/** Gates nothing. Used until `GET /permissions/seats` and the plan's seat both answer. */
const OPEN: PermissionGates = {
  reasonFor: () => null,
  unlockableByUpgrade: () => false,
  active: false,
  planTier: null,
  plansCarrying: () => [],
};

/**
 * The seat type a vessel's plan corresponds to, mirroring `subscriptionSeatType` in the
 * People service's `internal/app/permissions/helpers.go`: the tier's management seat, and
 * `harbor_seat` for the free tier, for a row the server treats as dead, and for a vessel
 * with no subscription at all.
 */
export const planSeatOf = (subscription: Subscription | null): SeatType => {
  if (!isUpdatable(subscription)) return SeatType.HARBOR_SEAT;
  return seatTypeFor(subscription.tier, 'management') ?? SeatType.HARBOR_SEAT;
};

/**
 * The gates in force for one edit target.
 *
 * `planSeat` is the seat the vessel's plan buys: `planSeatOf` for a caller that reads the
 * subscription, `GET /permissions/services/{id}/plan-seat` for anyone on the vessel. While it
 * is unknown — or empty, for a service no plan gates — nothing is attributed to the plan and
 * nothing is gated; the server still refuses what the plan does not cover.
 *
 * `targetSeat` is the seat that target is measured against: the person's own `seat_type`
 * when editing a person, and the vessel's plan seat when editing a role — the same seat
 * the server picks in each case.
 *
 * An unrecognised `targetSeat` falls back to the plan's allow-list. That is the closest
 * safe superset: a seat type this client has not been taught about is a new one, and
 * disabling every row on the page would read as broken rather than as gated.
 */
export const buildPermissionGates = (
  seatPermissions: readonly SeatPermissions[],
  planSeat: string | null,
  targetSeat: string | null,
): PermissionGates => {
  if (seatPermissions.length === 0 || !planSeat) return OPEN;

  const bySeat = new Map(
    seatPermissions.map((entry) => [entry.seat_type, new Set(entry.permissions ?? [])]),
  );

  const planAllowed = bySeat.get(planSeat) ?? new Set<string>();
  const seatAllowed = (targetSeat && bySeat.get(targetSeat)) || planAllowed;

  // What each tier's management seat carries. Upgrading swaps one of these in, so a
  // permission outside their union is out of reach of every plan there is to buy.
  const tiers = Object.values(SubscriptionTier).map((tier) => {
    const seat = seatTypeFor(tier, 'management');
    return { tier, seat, allowed: (seat && bySeat.get(seat)) || new Set<string>() };
  });
  const anyPlanAllowed = new Set(tiers.flatMap(({ allowed }) => [...allowed]));
  const planTier = tiers.find(({ seat }) => seat === planSeat)?.tier ?? null;

  return {
    active: true,
    planTier,
    plansCarrying: (names, match = 'all') =>
      tiers
        .filter(({ tier, allowed }) => {
          if (tier === planTier || names.length === 0) return false;
          const has = (name: string) => allowed.has(name);
          return match === 'any' ? names.some(has) : names.every(has);
        })
        .map(({ tier }) => tier),
    reasonFor: (name) => {
      if (seatAllowed.has(name)) return null;
      return planAllowed.has(name) ? 'seat' : 'plan';
    },
    unlockableByUpgrade: (name) => !planAllowed.has(name) && anyPlanAllowed.has(name),
  };
};
